import { inject, Injectable } from '@angular/core';
import { ConfigService } from '../shared/config/config.service';
import { Mode, MODE_CONFIGURATIONS, ModeConfiguration } from '../components/header/header-configuration';

/**
 * Expose le mode d'affichage du portail aux composants de layout (header,
 * footer).
 *
 * Le mode est lu à la racine de `assets/config.json` : il qualifie
 * l'application elle-même, pas la configuration Pégase qu'elle embarque.
 *
 * ```json
 * { "mode": "apprenant", "pegase": { "environnement": "test" } }
 * ```
 *
 * La valeur par défaut est {@link Mode.GESTIONNAIRE} : les applications
 * gestionnaire n'ont rien à déclarer, seules les applications apprenant
 * ajoutent la clé. Une valeur absente, inconnue ou une configuration pas encore
 * chargée retombent donc sur le mode gestionnaire plutôt que de lever.
 */
@Injectable({ providedIn: 'root' })
export class ModeService {
  private configService = inject(ConfigService, { optional: true });

  get mode(): Mode {
    return this.isMode(this.rawMode) ? this.rawMode : Mode.GESTIONNAIRE;
  }

  get configuration(): ModeConfiguration {
    return MODE_CONFIGURATIONS[this.mode];
  }

  /**
   * `ConfigService.getConfig()` lève tant que la configuration n'a pas été
   * chargée : on retombe silencieusement sur le mode par défaut, le layout ne
   * doit pas casser pour ça.
   */
  private get rawMode(): unknown {
    try {
      return this.configService?.getConfig()?.mode;
    } catch {
      return undefined;
    }
  }

  private isMode(value: unknown): value is Mode {
    return Object.values(Mode).includes(value as Mode);
  }
}